import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import WidgetExport from "./WidgetExport";

const SERIES = [
  { key: "uploads", label: "Uploads", color: "#6366F1" },
  { key: "calls", label: "Calls", color: "#0284C7" },
  { key: "captured", label: "Data Captured", color: "#059669" },
  { key: "callbacks", label: "Callbacks", color: "#D97706" },
  { key: "wrong", label: "Wrong Numbers", color: "#DC2626" },
];

/** Historical Trends tab — day/week/month series with custom date range
 *  (inherited from the global filter bar). */
export default function TrendsTab({ filters }) {
  const [granularity, setGranularity] = useState("day");
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { granularity };
    ["date_from", "date_to", "date_mode", "assignee", "classification",
     "disposition", "sector", "employee_count"].forEach((k) => { if (filters?.[k]) params[k] = filters[k]; });
    api.get("/leads/analytics/trends", { params })
      .then((r) => { if (alive) setSeries(r.data.series || []); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [granularity, filters]);

  return (
    <div className="space-y-4" data-testid="dash-tab-trends">
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Historical Trends</CardTitle>
          <div className="flex items-center gap-2">
            <Tabs value={granularity} onValueChange={setGranularity}>
              <TabsList className="h-7">
                <TabsTrigger value="day" className="text-[10px] px-2 py-0.5" data-testid="trend-gran-day">Daily</TabsTrigger>
                <TabsTrigger value="week" className="text-[10px] px-2 py-0.5" data-testid="trend-gran-week">Weekly</TabsTrigger>
                <TabsTrigger value="month" className="text-[10px] px-2 py-0.5" data-testid="trend-gran-month">Monthly</TabsTrigger>
              </TabsList>
            </Tabs>
            <WidgetExport title="Trends" headers={["Period", ...SERIES.map((s) => s.label)]}
                          getRows={() => series.map((r) => [r.period, ...SERIES.map((s) => r[s.key] || 0)])} />
          </div>
        </CardHeader>
        <CardContent data-testid="trends-chart">
          <p className="text-[10px] text-slate-400 mb-2">
            Use the date filters above for a custom range. Dispositions, calls and completion over time.
          </p>
          {loading ? (
            <div className="text-xs text-slate-400 p-6">Loading…</div>
          ) : series.length === 0 ? (
            <div className="text-xs text-slate-400 p-6">No activity in the selected range.</div>
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer debounce={50}>
                <LineChart data={series} margin={{ top: 4, right: 12, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {SERIES.map((s) => (
                    <Line key={s.key} type="monotone" dataKey={s.key} name={s.label}
                          stroke={s.color} strokeWidth={2} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {series.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Period Breakdown</CardTitle></CardHeader>
          <CardContent className="p-2" data-testid="trends-table">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr>
                  <th className="text-left px-2 py-1">Period</th>
                  {SERIES.map((s) => <th key={s.key} className="text-right px-2">{s.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {series.map((r) => (
                  <tr key={r.period} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-mono text-[10px]">{r.period}</td>
                    {SERIES.map((s) => (
                      <td key={s.key} className="text-right px-2 font-mono">{r[s.key] || 0}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
